import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import Heading from 'components/partials/heading/heading';
import Loader from 'components/partials/loader/loader';
import Button from 'components/partials/button/button';
import Select from 'components/partials/select/select';
import MonitoringSettingsForm from 'containers/admin/clients/client/tabs-components/monitoring-settings-modal/monitoring-settings-form';
import CriticalElementsTable from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-table/critical-elements-table';
import useCriticalElements from 'containers/admin/clients/client/tabs-components/critical-elements/hooks/use-critical-elements';
import { generateYearOptions } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-filters/goals-matrix-filters';
import type { YearOption } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-filters/goals-matrix-filters';
import { getCurrentYear } from 'helpers/date.helpers';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { renderModal } from 'helpers/modal-render';

import Icons from 'assets/icons';

interface CurrentUserShape {
  data?: {
    oktaUserId?: string;
    admin?: boolean;
  };
}

interface CriticalElementsTabProps {
  clientId: string | number;
}

const CriticalElementsTab = ({
  clientId,
}: CriticalElementsTabProps): React.JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = getCurrentYear();
  const currentUser = useCurrentUser() as CurrentUserShape;

  const pathParts = location.pathname.split('/');
  const criticalElementsIndex = pathParts.indexOf('critical-elements');
  const yearFromPath =
    criticalElementsIndex !== -1 && pathParts.length > criticalElementsIndex + 1
      ? pathParts[criticalElementsIndex + 1]
      : null;

  const [selectedYear, setSelectedYear] = useState<number>(
    yearFromPath && !Number.isNaN(Number.parseInt(yearFromPath, 10))
      ? Number.parseInt(yearFromPath, 10)
      : currentYear
  );

  const { criticalElements, loading } = useCriticalElements(
    clientId,
    selectedYear
  );

  useEffect(() => {
    if (location.pathname.endsWith('/critical-elements')) {
      navigate(`/admin/clients/${clientId}/critical-elements/${selectedYear}`, {
        replace: true,
      });
    }
  }, [location.pathname, clientId, navigate, selectedYear]);

  const handleViewDetails = (id: string | number): void => {
    navigate(
      `/admin/clients/${clientId}/critical-elements/${selectedYear}/${id}`
    );
  };

  const handleAddNew = (): void => {
    navigate(
      `/admin/clients/${clientId}/critical-elements/${selectedYear}/new`
    );
  };

  const handleYearChange = (year: number): void => {
    setSelectedYear(year);
    navigate(`/admin/clients/${clientId}/critical-elements/${year}`, {
      replace: true,
    });
  };

  const years = generateYearOptions({
    futureYears: 3,
    pastYears: 2,
  });

  const openMonitoringSettingsModal = (): void => {
    const userId = currentUser?.data?.oktaUserId;
    const formId = `monitoring-settings-form-${Date.now()}`;

    renderModal({
      headerText: 'Client monitoring settings',
      children: (
        <MonitoringSettingsForm
          formId={formId}
          clientId={clientId}
          userId={userId}
        />
      ),
      formId,
      onConfirmButtonLabel: 'Submit',
    } as never);
  };

  return (
    <div className="relative flex flex-1 flex-col">
      <div className="flex justify-between items-center my-5">
        <div className="flex items-center gap-4">
          <Heading variant="h2">Critical Elements</Heading>
          <Select<YearOption>
            options={years}
            value={years.find((year) => year.value === selectedYear)}
            onChange={(option) => {
              if (option && !Array.isArray(option)) {
                handleYearChange(option.value);
              }
            }}
            selectWrapperClass="!w-28"
            extraBasicStyles={{ padding: 0, height: '2' }}
            aria-label="Select year"
          />
        </div>

        {currentUser.data?.admin && (
          <div className="flex items-center gap-4">
            <Button
              iconBefore={<Icons.Gear aria-label="MonitoringSettingsIcon" />}
              variant="reserve-outlined"
              size="md"
              onClick={openMonitoringSettingsModal}
            >
              Monitoring settings
            </Button>

            <Button
              variant="brand-filled"
              onClick={handleAddNew}
              size="md"
              iconBefore={<Icons.Plus aria-label="PlusIcon" />}
            >
              Add New
            </Button>
          </div>
        )}
      </div>

      <div className="relative flex flex-1 flex-col">
        {loading && (
          <Loader loading customClass="!bg-white" fullScreen={false} />
        )}

        <CriticalElementsTable
          criticalElements={criticalElements}
          loading={loading}
          handleViewDetails={handleViewDetails}
          clientId={clientId}
          selectedYear={selectedYear}
        />
      </div>
    </div>
  );
};

export default CriticalElementsTab;
